"use client";

/**
 * P4-02/03 · A cart line row, shared across surfaces (P4-DB01 §03).
 *
 * `layout="drawer"` (mini-cart) packs the price beside the stepper; `"page"`
 * (full cart) links the title to the PDP, right-aligns the line total, and adds
 * a Remove action row. The slots and their order are shared. Money comes from
 * the server-built `CartLineView`; the client never recomputes a line total.
 */
import { LocaleLink, useTranslator } from "~/i18n/provider";
import { StoreImage } from "~/components/ui/store-image";
import { Badge } from "~/components/ui/badge";
import { QuantityStepper } from "~/components/product/actions/QuantityStepper";
import { PriceDisplay } from "~/components/storefront/product-card/price-display";
import { formatMoney } from "~/lib/money";
import { cn } from "~/lib/utils";
import type { CartLineView } from "~/lib/cart/summary";

export function CartLineItem({
  line,
  layout = "drawer",
  pending = false,
  onQuantityChange,
  onRemove,
}: {
  line: CartLineView;
  layout?: "drawer" | "page";
  /** True while a qty/remove mutation for this line is in flight. */
  pending?: boolean;
  onQuantityChange: (quantity: number) => void;
  onRemove: () => void;
}) {
  const t = useTranslator();
  const {
    productSlug,
    title,
    optionLabel,
    quantity,
    maxQuantity,
    inStock,
    priceChanged,
    stockIssue,
    availableStock,
    unitPriceMinorAtAdd,
    lineTotalMinor,
    compareAtMinor,
    lineDiscountMinor,
    currency,
    thumbnailUrl,
    thumbnailAlt,
  } = line;

  const isPage = layout === "page";
  const lineCompareAt =
    compareAtMinor != null ? compareAtMinor * quantity : null;
  const previousLineTotal = unitPriceMinorAtAdd * quantity;

  // P6-09 · A per-line automatic/catalog discount strikes the line: show the
  // discounted total with the pre-discount total struck through (this takes
  // visual precedence over the compare-at / price-changed treatments).
  const promoDiscount = inStock ? (lineDiscountMinor ?? 0) : 0;
  const hasPromo = promoDiscount > 0;
  const displayPrice = hasPromo
    ? lineTotalMinor - promoDiscount
    : lineTotalMinor;
  const displayCompareAt = hasPromo ? lineTotalMinor : lineCompareAt;
  const displayChanged = priceChanged && !hasPromo;
  const promoBadge = hasPromo ? (
    <Badge tone="success">{t("cart.offerApplied")}</Badge>
  ) : null;

  const spinner = (
    <span
      className="inline-flex items-center"
      role="status"
      aria-label={t("cart.updating")}
    >
      <span
        aria-hidden="true"
        className="size-4 animate-spin rounded-full border-2 border-border border-t-secondary"
      />
    </span>
  );

  const stepper = inStock ? (
    <QuantityStepper
      value={quantity}
      max={maxQuantity}
      onChange={onQuantityChange}
      disabled={pending}
    />
  ) : null;

  const thumb = (
    <div
      className={cn(
        "relative aspect-[4/5] shrink-0 overflow-hidden rounded-md bg-surface-2",
        isPage ? "w-20" : "w-14",
        !inStock && "opacity-50 grayscale",
      )}
    >
      {thumbnailUrl ? (
        <StoreImage
          src={thumbnailUrl}
          alt={thumbnailAlt ?? ""}
          preset="listThumb"
          fill
          className="object-cover"
        />
      ) : null}
    </div>
  );

  const heading = isPage ? (
    <LocaleLink
      href={`/products/${productSlug}`}
      className={cn(
        // A link is inline, and an inline box ignores `truncate`, so a long
        // title ran out of the card and under the price on a phone.
        // `line-clamp-2` makes it a block-level box (-webkit-box) and caps it
        // at two lines — adding `block` would override that and undo the clamp.
        "line-clamp-2 break-words text-sm font-semibold hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]",
        inStock ? "text-fg" : "text-fg-muted",
      )}
    >
      {title}
    </LocaleLink>
  ) : (
    <p
      className={cn(
        "truncate text-sm font-semibold",
        inStock ? "text-fg" : "text-fg-muted",
      )}
    >
      {title}
    </p>
  );

  // ── Full-cart page layout ────────────────────────────────────────────────
  // One grid, one price. Below `sm` there is no room for a price column beside
  // the stepper and a "Remove" link (≈215 px for all three at 375 px), so a
  // phone gets the drawer's arrangement — × beside the title, the price on the
  // stepper's row. From `sm` up the price spans both rows on the right and
  // "Remove" sits by the stepper, as before. The price is placed, not
  // duplicated, so assistive tech and tests see it once.
  if (isPage) {
    return (
      <div className="grid grid-cols-[auto_minmax(0,1fr)_auto] gap-x-3 border-b border-border py-4 last:border-b-0 sm:gap-x-4">
        <div className="row-span-2">{thumb}</div>
        {/* On a phone the title row spans the price column too, so the title
            gets the width and the × sits at its end; from sm it is one cell. */}
        <div className="col-span-2 col-start-2 row-start-1 flex min-w-0 items-start justify-between gap-2 sm:col-span-1">
          <div className="min-w-0">
            {heading}
            {optionLabel ? (
              <p className="mt-0.5 truncate text-xs text-fg-muted">
                {optionLabel}
              </p>
            ) : null}
            {stockIssue || priceChanged || hasPromo ? (
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {stockIssue === "OUT" ? (
                  <Badge tone="neutral">{t("cart.outOfStock")}</Badge>
                ) : stockIssue === "LOW" ? (
                  <Badge tone="warning">Only {availableStock} left</Badge>
                ) : null}
                {priceChanged ? (
                  <Badge tone="warning">{t("cart.priceChanged")}</Badge>
                ) : null}
                {promoBadge}
              </div>
            ) : null}
          </div>
          <button
            type="button"
            aria-label={`Remove ${title}`}
            onClick={onRemove}
            disabled={pending}
            className="-mr-1 -mt-1 shrink-0 rounded-md p-1.5 text-fg-muted hover:bg-surface-2 hover:text-fg disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)] sm:hidden"
          >
            <span aria-hidden="true">×</span>
          </button>
        </div>
        <div className="col-start-2 row-start-2 mt-3 flex items-center gap-4">
          {stepper}
          <button
            type="button"
            onClick={onRemove}
            disabled={pending}
            className="hidden text-xs font-semibold text-secondary hover:underline disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)] sm:inline"
          >
            Remove
          </button>
        </div>
        <div className="col-start-3 row-start-2 mt-3 self-center text-right sm:row-span-2 sm:row-start-1 sm:mt-0 sm:self-start">
          {pending ? (
            spinner
          ) : inStock ? (
            <CartLinePrice
              priceMinor={displayPrice}
              compareAtMinor={displayCompareAt}
              previousPriceMinor={previousLineTotal}
              changed={displayChanged}
              currency={currency}
              className="justify-end"
            />
          ) : null}
        </div>
      </div>
    );
  }

  // ── Mini-cart drawer layout ──────────────────────────────────────────────
  return (
    <div className="flex gap-3 border-b border-border py-3 last:border-b-0">
      {thumb}
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            {heading}
            {optionLabel ? (
              <p className="mt-0.5 truncate text-xs text-fg-muted">
                {optionLabel}
              </p>
            ) : null}
          </div>
          <button
            type="button"
            aria-label={`Remove ${title}`}
            onClick={onRemove}
            disabled={pending}
            className="shrink-0 rounded-md p-1 text-fg-muted hover:bg-surface-2 hover:text-fg disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
          >
            <span aria-hidden="true">×</span>
          </button>
        </div>

        {stockIssue || priceChanged || hasPromo ? (
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {stockIssue === "OUT" ? (
              <Badge tone="neutral">{t("cart.outOfStock")}</Badge>
            ) : stockIssue === "LOW" ? (
              <Badge tone="warning">Only {availableStock} left</Badge>
            ) : null}
            {priceChanged ? (
              <Badge tone="warning">{t("cart.priceChanged")}</Badge>
            ) : null}
            {promoBadge}
          </div>
        ) : null}

        <div className="mt-2 flex items-center justify-between gap-2">
          {inStock ? (
            stepper
          ) : (
            <button
              type="button"
              onClick={onRemove}
              disabled={pending}
              className="text-xs font-semibold text-secondary hover:underline disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
            >
              Remove
            </button>
          )}
          {pending ? (
            spinner
          ) : inStock ? (
            <CartLinePrice
              priceMinor={displayPrice}
              compareAtMinor={displayCompareAt}
              previousPriceMinor={previousLineTotal}
              changed={displayChanged}
              currency={currency}
              className="text-right"
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}

function CartLinePrice({
  priceMinor,
  compareAtMinor,
  previousPriceMinor,
  changed,
  currency,
  className,
}: {
  priceMinor: number;
  compareAtMinor: number | null;
  previousPriceMinor: number;
  changed: boolean;
  currency: string;
  className?: string;
}) {
  if (!changed) {
    return (
      <PriceDisplay
        priceMinor={priceMinor}
        compareAtMinor={compareAtMinor}
        currency={currency}
        className={className}
      />
    );
  }

  const current = formatMoney(priceMinor, currency);
  const previous = formatMoney(previousPriceMinor, currency);
  return (
    <span className={cn("flex flex-col items-end", className)}>
      <span className="sr-only">{`Current ${current}, previously ${previous}`}</span>
      <span aria-hidden className="text-sm font-semibold text-warning">
        {current}
      </span>
      <s aria-hidden className="text-xs text-fg-muted">
        {previous}
      </s>
    </span>
  );
}
